import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, User, Users, Building2,
  CircleAlert, ShieldCheck, Info, Loader2
} from 'lucide-react';
import ClienteBuscador from '../../components/ClienteBuscador';
import FactorBadge from '../../components/FactorBadge';
import {
  MODALIDADES_POR_TIPO,
  productosCompatibles, obtenerProductoPorCodigo,
  describirFrecuencia, describirPlazo, describirPagosTipo
} from '../../data/productosVigentes';
import { formatoMoneda } from '../../utils/finanzas';
import { creditosAPI, agrupacionesAPI, productosFinancierosAPI, describirError } from '../../services/api';
import '../../styles/Pages.css';
import '../../styles/CreditoNuevo.css';

// Debounce de la previsualización (Fase 5.2, sección 5): no se llama al
// backend en cada tecla, se espera a que el usuario haga una pausa.
const DEBOUNCE_PREVIEW_MS = 400;

/** Texto de una celda del resumen que depende de la previsualización del backend. */
function textoPreview(cargando, calculoValidado, valor) {
  if (cargando) return 'Calculando…';
  if (!calculoValidado || valor == null) return 'Pendiente de parametrización';
  return formatoMoneda(valor);
}

// Fase 6.2: GRUPAL/COLOCADORA ya no capturan participantes embebidos aquí.
// Este wizard sólo crea la AgrupacionCredito (nombre + responsable); cada
// integrante se agrega como SU PROPIO crédito desde el expediente de la
// agrupación (/agrupaciones/:id), que es donde también se previsualiza y
// se autoriza — ver docs/ARQUITECTURA_AGRUPACIONES_PAGOS_FASE_6_1.md.
const PASOS_PERSONAL = ['Tipo', 'Titular', 'Producto / Plazo', 'Operación', 'Garantía', 'Revisión'];
const PASOS_AGRUPACION = ['Tipo', 'Datos de la agrupación', 'Revisión'];

const TIPOS = [
  { valor: 'PERSONAL', etiqueta: 'Personal', icono: User, texto: 'Titular único.' },
  { valor: 'GRUPAL', etiqueta: 'Grupal', icono: Users, texto: 'Grupo con integrantes, cada uno con crédito propio.' },
  { valor: 'COLOCADORA', etiqueta: 'Colocadora', icono: Building2, texto: 'Cartera administrada por un tercero; cada cliente final tiene crédito propio.' }
];

const GARANTIA_TIPOS = ['Aval', 'Prendaria', 'Hipotecaria', 'Sin garantía específica'];

function CreditoNuevo() {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(0);

  const [tipoCredito, setTipoCredito] = useState(null);
  const esAgrupacion = tipoCredito === 'GRUPAL' || tipoCredito === 'COLOCADORA';
  const PASOS = tipoCredito === 'PERSONAL' ? PASOS_PERSONAL : PASOS_AGRUPACION;

  // --- Estado PERSONAL ---
  const [titular, setTitular] = useState(null);
  const [modalidad, setModalidad] = useState(null);
  const [productoCodigo, setProductoCodigo] = useState(null);
  const [monto, setMonto] = useState('');
  const [fechaDesembolso, setFechaDesembolso] = useState('');
  const [garantia, setGarantia] = useState({ tipo: '', descripcion: '', valor: '', observaciones: '' });
  const [agente, setAgente] = useState('');

  // --- Estado GRUPAL/COLOCADORA (sólo la agrupación) ---
  const [nombreAgrupacion, setNombreAgrupacion] = useState('');
  const [responsable, setResponsable] = useState(null);

  const [productosApi, setProductosApi] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState(null);

  // Previsualización financiera (Fase 5.2/6.2): SIEMPRE viene del backend,
  // sólo aplica al flujo PERSONAL — GRUPAL/COLOCADORA no calculan nada
  // financiero en este wizard (no hay monto ni producto que previsualizar
  // todavía; eso ocurre por integrante, en el expediente de la agrupación).
  const [preview, setPreview] = useState(null);
  const [previewCargando, setPreviewCargando] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewAbortRef = useRef(null);
  const previewTimerRef = useRef(null);

  useEffect(() => {
    productosFinancierosAPI
      .listar({ activo: true })
      .then((res) => setProductosApi(res.data?.data || []))
      .catch(() => setProductosApi([]));
  }, []);

  const producto = useMemo(() => obtenerProductoPorCodigo(productoCodigo), [productoCodigo]);
  const productoApi = useMemo(
    () => productosApi.find((p) => p.codigo === productoCodigo) || null,
    [productosApi, productoCodigo]
  );

  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (previewAbortRef.current) previewAbortRef.current.abort();

    if (tipoCredito !== 'PERSONAL' || !productoApi?.versionVigente || !monto || Number(monto) <= 0 || !fechaDesembolso) {
      setPreview(null);
      setPreviewError(null);
      setPreviewCargando(false);
      return undefined;
    }

    setPreviewCargando(true);
    previewTimerRef.current = setTimeout(() => {
      const controller = new AbortController();
      previewAbortRef.current = controller;

      creditosAPI
        .previsualizarBorrador(
          {
            clienteTitularId: titular?.id,
            tipoCredito: 'PERSONAL',
            productoVersionId: productoApi.versionVigente.id,
            montoSolicitado: Number(monto),
            fechaDesembolso
          },
          { signal: controller.signal }
        )
        .then(({ data }) => {
          setPreview(data);
          setPreviewError(null);
        })
        .catch((err) => {
          if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
          setPreview(null);
          setPreviewError(describirError(err).mensaje);
        })
        .finally(() => setPreviewCargando(false));
    }, DEBOUNCE_PREVIEW_MS);

    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCredito, productoApi, monto, fechaDesembolso, titular]);

  const factorValidado = preview?.calculoValidado === true;
  const primerVencimiento = preview?.primerVencimiento || null;

  const modalidadesDisponibles = tipoCredito === 'PERSONAL' ? MODALIDADES_POR_TIPO.PERSONAL : [];
  const productosDisponibles = tipoCredito === 'PERSONAL' && modalidad ? productosCompatibles('PERSONAL', modalidad) : [];

  const puedeAvanzar = () => {
    const etiqueta = PASOS[paso];
    switch (etiqueta) {
      case 'Tipo': return !!tipoCredito;
      case 'Titular': return !!titular;
      case 'Producto / Plazo': return !!productoCodigo;
      case 'Operación': return !!monto && Number(monto) > 0 && !!fechaDesembolso;
      case 'Garantía': return true;
      case 'Datos de la agrupación':
        return !!nombreAgrupacion.trim() && (tipoCredito !== 'COLOCADORA' || !!responsable);
      default: return true;
    }
  };

  const siguiente = () => setPaso((p) => Math.min(PASOS.length - 1, p + 1));
  const anterior = () => setPaso((p) => Math.max(0, p - 1));

  const irAPaso = (indice) => {
    if (indice <= paso) setPaso(indice);
  };

  const puedeGuardarPersonal =
    !!titular && !!productoCodigo && !!monto && Number(monto) > 0 && !!fechaDesembolso;
  const puedeGuardarAgrupacion =
    !!nombreAgrupacion.trim() && (tipoCredito !== 'COLOCADORA' || !!responsable);
  const puedeGuardar = tipoCredito === 'PERSONAL' ? puedeGuardarPersonal : puedeGuardarAgrupacion;

  const handleGuardarPersonal = async () => {
    const productoSeleccionado = productosApi.find((p) => p.codigo === productoCodigo);
    if (!productoSeleccionado) {
      setErrorGuardado('No fue posible localizar el producto financiero seleccionado. Intenta de nuevo.');
      return;
    }
    const { data: credito } = await creditosAPI.crear({
      clienteTitularId: titular.id,
      tipoCredito: 'PERSONAL',
      productoId: productoSeleccionado.id,
      montoSolicitado: Number(monto),
      fechaDesembolso,
      tipoGarantia: garantia.tipo || undefined,
      garantiaDescripcion: garantia.descripcion || undefined,
      valorGarantia: garantia.valor ? Number(garantia.valor) : undefined,
      observaciones: garantia.observaciones || undefined,
      agente: agente || undefined
    });
    navigate(`/creditos/${credito.id}`);
  };

  const handleGuardarAgrupacion = async () => {
    const { data: agrupacion } = await agrupacionesAPI.crear({
      tipoAgrupacion: tipoCredito,
      nombre: nombreAgrupacion.trim(),
      clienteResponsableId: responsable?.id || undefined
    });
    navigate(`/agrupaciones/${agrupacion.id}`);
  };

  const handleGuardar = async () => {
    if (!puedeGuardar || guardando) return;
    setErrorGuardado(null);
    setGuardando(true);
    try {
      if (tipoCredito === 'PERSONAL') await handleGuardarPersonal();
      else await handleGuardarAgrupacion();
    } catch (error) {
      setErrorGuardado(describirError(error).mensaje);
    } finally {
      setGuardando(false);
    }
  };

  const etiquetaPasoActual = PASOS[paso];

  return (
    <div className="page-container credito-nuevo">
      <button className="btn btn-ghost volver" onClick={() => navigate('/creditos')}>
        <ArrowLeft size={16} strokeWidth={1.75} />
        Volver a créditos
      </button>

      <ol className="stepper" aria-label="Progreso del alta de crédito">
        {PASOS.map((etiqueta, i) => (
          <li
            key={etiqueta}
            className={`stepper-item ${i === paso ? 'is-current' : ''} ${i < paso ? 'is-done' : ''}`}
          >
            <button
              type="button"
              className="stepper-button"
              onClick={() => irAPaso(i)}
              disabled={i > paso}
            >
              <span className="stepper-index">{i < paso ? <Check size={13} strokeWidth={2.5} /> : i + 1}</span>
              <span className="stepper-label">{etiqueta}</span>
            </button>
          </li>
        ))}
      </ol>

      <div className="credito-nuevo-panel">
        {etiquetaPasoActual === 'Tipo' && (
          <section className="paso-seccion">
            <h2>Tipo de crédito</h2>
            <div className="tipo-cards">
              {TIPOS.map(({ valor, etiqueta, icono: Icono, texto }) => (
                <button
                  key={valor}
                  type="button"
                  className={`tipo-card ${tipoCredito === valor ? 'is-selected' : ''}`}
                  onClick={() => {
                    setTipoCredito(valor);
                    setModalidad(null);
                    setProductoCodigo(null);
                  }}
                >
                  <Icono size={22} strokeWidth={1.75} />
                  <span className="tipo-card-titulo">{etiqueta}</span>
                  <span className="tipo-card-texto">{texto}</span>
                </button>
              ))}
            </div>

            {esAgrupacion && (
              <div className="data-notice">
                <Info size={16} strokeWidth={1.75} />
                <span>
                  Este paso sólo crea la agrupación. Cada integrante se agrega después, en el
                  expediente de la agrupación, con su propio crédito, folio y calendario.
                </span>
              </div>
            )}
          </section>
        )}

        {etiquetaPasoActual === 'Titular' && (
          <section className="paso-seccion">
            <h2>Titular</h2>
            <p className="paso-ayuda">Busca y selecciona al titular del crédito.</p>
            <ClienteBuscador clienteSeleccionado={titular} onSeleccionar={setTitular} />
          </section>
        )}

        {etiquetaPasoActual === 'Producto / Plazo' && (
          <section className="paso-seccion">
            <h2>Producto / Plazo</h2>

            <div className="paso-subseccion">
              <h3>Modalidad</h3>
              <div className="modalidad-opciones">
                {modalidadesDisponibles.map((m) => (
                  <button
                    key={m.valor}
                    type="button"
                    className={`chip-opcion ${modalidad === m.valor ? 'is-selected' : ''}`}
                    onClick={() => { setModalidad(m.valor); setProductoCodigo(null); }}
                  >
                    {m.etiqueta}
                  </button>
                ))}
              </div>
            </div>

            {modalidad && (
              productosDisponibles.length === 0 ? (
                <p className="paso-ayuda">No hay productos disponibles para esta combinación.</p>
              ) : (
                <div className="producto-grid">
                  {productosDisponibles.map((p) => (
                    <button
                      key={p.codigo}
                      type="button"
                      className={`producto-card ${productoCodigo === p.codigo ? 'is-selected' : ''}`}
                      onClick={() => setProductoCodigo(p.codigo)}
                    >
                      <span className="producto-card-nombre">{p.legacyName}</span>
                      <span className="producto-card-frecuencia">{describirPagosTipo(p)}</span>
                      <FactorBadge codigoProducto={p.codigo} />
                    </button>
                  ))}
                </div>
              )
            )}
          </section>
        )}

        {etiquetaPasoActual === 'Operación' && producto && (
          <section className="paso-seccion">
            <h2>Operación</h2>

            <div className="form-grid-2">
              <label className="campo">
                <span>Monto solicitado</span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="Ej: 10000"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                />
              </label>

              <label className="campo">
                <span>Fecha de desembolso</span>
                <input
                  type="date"
                  value={fechaDesembolso}
                  onChange={(e) => setFechaDesembolso(e.target.value)}
                />
              </label>
            </div>

            {(!monto || Number(monto) <= 0 || !fechaDesembolso) && (
              <div className="data-notice">
                <Info size={16} strokeWidth={1.75} />
                <span>Ingresa monto y fecha de desembolso para calcular la previsualización.</span>
              </div>
            )}

            {monto && Number(monto) > 0 && fechaDesembolso && previewCargando && (
              <div className="data-notice">
                <Loader2 size={16} strokeWidth={1.75} className="spin" />
                <span>Calculando…</span>
              </div>
            )}

            {previewError && (
              <div className="data-notice is-error">
                <CircleAlert size={16} strokeWidth={1.75} />
                <span>{previewError}</span>
              </div>
            )}

            {!previewCargando && !previewError && preview && preview.calculoValidado === false && (
              <div className="data-notice">
                <CircleAlert size={16} strokeWidth={1.75} />
                <span>
                  Este producto todavía no tiene un cálculo validado. No se muestra un pago
                  estimado para evitar presentar una cifra no confirmada.
                </span>
              </div>
            )}

            {!previewCargando && factorValidado && preview && (
              <div className="preview-pago">
                <div className="preview-pago-grid">
                  <div><span>Número de pagos</span><strong>{preview.numeroPagos}</strong></div>
                  <div><span>Frecuencia</span><strong>{describirFrecuencia(producto)}</strong></div>
                  <div><span>Pago regular</span><strong>{formatoMoneda(preview.pagoRegular)}</strong></div>
                  <div><span>Última cuota</span><strong>{formatoMoneda(preview.ultimaCuota)}</strong></div>
                  <div><span>Total a pagar</span><strong>{formatoMoneda(preview.totalPagar)}</strong></div>
                  {primerVencimiento && (
                    <div><span>Primer vencimiento</span><strong>{primerVencimiento}</strong></div>
                  )}
                </div>
                <p className="preview-pago-nota">
                  <Info size={13} strokeWidth={1.75} />
                  Cálculo validado con datos reales de operación.
                </p>
              </div>
            )}
          </section>
        )}

        {etiquetaPasoActual === 'Garantía' && (
          <section className="paso-seccion">
            <h2>Garantía</h2>
            <div className="form-grid-2">
              <label className="campo">
                <span>Tipo de garantía</span>
                <select value={garantia.tipo} onChange={(e) => setGarantia((g) => ({ ...g, tipo: e.target.value }))}>
                  <option value="">Selecciona…</option>
                  {GARANTIA_TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="campo">
                <span>Valor de garantía</span>
                <input
                  type="number"
                  placeholder="Ej: 15000"
                  value={garantia.valor}
                  onChange={(e) => setGarantia((g) => ({ ...g, valor: e.target.value }))}
                />
              </label>
              <label className="campo campo-ancho">
                <span>Descripción de garantía</span>
                <input
                  type="text"
                  placeholder="Ej: Refrigerador comercial, factura adjunta"
                  value={garantia.descripcion}
                  onChange={(e) => setGarantia((g) => ({ ...g, descripcion: e.target.value }))}
                />
              </label>
              <label className="campo campo-ancho">
                <span>Observaciones</span>
                <input
                  type="text"
                  value={garantia.observaciones}
                  onChange={(e) => setGarantia((g) => ({ ...g, observaciones: e.target.value }))}
                />
              </label>
            </div>

            <div className="paso-subseccion">
              <h3>Agente / Responsable</h3>
              <p className="paso-ayuda">Captura libre por ahora; el catálogo de usuarios llegará más adelante.</p>
              <label className="campo campo-ancho">
                <span>Agente / Responsable</span>
                <input
                  type="text"
                  placeholder="Ej: Karla Domínguez"
                  value={agente}
                  onChange={(e) => setAgente(e.target.value)}
                />
              </label>
            </div>
          </section>
        )}

        {etiquetaPasoActual === 'Datos de la agrupación' && (
          <section className="paso-seccion">
            <h2>Datos de la agrupación</h2>
            <p className="paso-ayuda">
              {tipoCredito === 'GRUPAL'
                ? 'Nombre del grupo. El representante es opcional.'
                : 'Nombre de la colocadora y el cliente responsable (obligatorio).'}
            </p>

            <label className="campo campo-ancho">
              <span>{tipoCredito === 'GRUPAL' ? 'Nombre del grupo' : 'Nombre de la colocadora'}</span>
              <input
                type="text"
                placeholder={tipoCredito === 'GRUPAL' ? 'Ej: Grupo Los Girasoles' : 'Ej: Juana Hernández'}
                value={nombreAgrupacion}
                onChange={(e) => setNombreAgrupacion(e.target.value)}
              />
            </label>

            <div className="paso-subseccion">
              <h3>{tipoCredito === 'GRUPAL' ? 'Representante (opcional)' : 'Cliente responsable'}</h3>
              <ClienteBuscador clienteSeleccionado={responsable} onSeleccionar={setResponsable} />
              {tipoCredito === 'COLOCADORA' && !responsable && (
                <div className="data-notice">
                  <CircleAlert size={16} strokeWidth={1.75} />
                  <span>Una COLOCADORA requiere un cliente responsable.</span>
                </div>
              )}
            </div>
          </section>
        )}

        {etiquetaPasoActual === 'Revisión' && tipoCredito === 'PERSONAL' && (
          <section className="paso-seccion">
            <h2>Resumen del crédito</h2>

            <dl className="resumen-grid">
              <div><dt>Titular</dt><dd>{titular?.nombreCompleto || '—'}</dd></div>
              <div><dt>Tipo</dt><dd>PERSONAL</dd></div>
              <div><dt>Producto</dt><dd>{producto?.legacyName || '—'}</dd></div>
              <div><dt>Plazo</dt><dd>{producto ? describirPlazo(producto) : '—'}</dd></div>
              <div><dt>Frecuencia</dt><dd>{producto ? describirFrecuencia(producto) : '—'}</dd></div>
              <div><dt>Monto</dt><dd>{formatoMoneda(Number(monto))}</dd></div>
              <div><dt>Pago regular</dt><dd>{textoPreview(previewCargando, factorValidado, preview?.pagoRegular)}</dd></div>
              <div><dt>Última cuota</dt><dd>{textoPreview(previewCargando, factorValidado, preview?.ultimaCuota)}</dd></div>
              <div><dt>Total a pagar</dt><dd>{textoPreview(previewCargando, factorValidado, preview?.totalPagar)}</dd></div>
              <div><dt>Fecha desembolso</dt><dd>{fechaDesembolso || '—'}</dd></div>
              <div><dt>Primer vencimiento</dt><dd>{primerVencimiento || '—'}</dd></div>
              <div><dt>Garantía</dt><dd>{garantia.tipo ? `${garantia.tipo} — ${formatoMoneda(Number(garantia.valor) || 0)}` : 'Sin garantía capturada'}</dd></div>
              <div><dt>Agente</dt><dd>{agente || 'Sin asignar'}</dd></div>
            </dl>

            {!previewCargando && !factorValidado && producto && (
              <div className="data-notice">
                <CircleAlert size={16} strokeWidth={1.75} />
                <span>Este producto no tiene un cálculo validado. Podrás guardar el crédito como borrador, pero no podrá autorizarse hasta contar con parametrización confirmada.</span>
              </div>
            )}

            {errorGuardado && (
              <div className="data-notice is-error">
                <CircleAlert size={16} strokeWidth={1.75} />
                <span>{errorGuardado}</span>
              </div>
            )}

            <div className="resumen-acciones">
              <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={!puedeGuardar || guardando}>
                {guardando ? <Loader2 size={16} strokeWidth={1.75} className="spin" /> : <ShieldCheck size={16} strokeWidth={1.75} />}
                {guardando ? 'Guardando…' : 'Guardar crédito (borrador)'}
              </button>
              <p className="resumen-nota">
                El crédito se guarda como BORRADOR. La autorización final se hace desde el expediente del crédito.
              </p>
            </div>
          </section>
        )}

        {etiquetaPasoActual === 'Revisión' && esAgrupacion && (
          <section className="paso-seccion">
            <h2>Resumen de la agrupación</h2>

            <dl className="resumen-grid">
              <div><dt>Tipo</dt><dd>{tipoCredito}</dd></div>
              <div><dt>Nombre</dt><dd>{nombreAgrupacion || '—'}</dd></div>
              <div><dt>{tipoCredito === 'GRUPAL' ? 'Representante' : 'Responsable'}</dt><dd>{responsable?.nombreCompleto || 'Sin asignar'}</dd></div>
            </dl>

            <div className="data-notice">
              <Info size={16} strokeWidth={1.75} />
              <span>
                Al guardar se crea la agrupación (sin créditos todavía). El siguiente paso —
                agregar cada integrante con su propio crédito — se hace en el expediente de la
                agrupación.
              </span>
            </div>

            {errorGuardado && (
              <div className="data-notice is-error">
                <CircleAlert size={16} strokeWidth={1.75} />
                <span>{errorGuardado}</span>
              </div>
            )}

            <div className="resumen-acciones">
              <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={!puedeGuardar || guardando}>
                {guardando ? <Loader2 size={16} strokeWidth={1.75} className="spin" /> : <ShieldCheck size={16} strokeWidth={1.75} />}
                {guardando ? 'Guardando…' : 'Crear agrupación'}
              </button>
              <p className="resumen-nota">Después podrás agregar integrantes desde el expediente de la agrupación.</p>
            </div>
          </section>
        )}
      </div>

      <div className="credito-nuevo-nav">
        <button type="button" className="btn btn-secondary" onClick={anterior} disabled={paso === 0}>
          <ArrowLeft size={16} strokeWidth={1.75} /> Anterior
        </button>
        {paso < PASOS.length - 1 && (
          <button type="button" className="btn btn-primary" onClick={siguiente} disabled={!puedeAvanzar()}>
            Siguiente <ArrowRight size={16} strokeWidth={1.75} />
          </button>
        )}
      </div>
    </div>
  );
}

export default CreditoNuevo;
